(() => {
  'use strict';
  const root = (window.SurfButta?.root || '/').replace(/\/?$/, '/');
  const drawer = document.querySelector('[data-cart-drawer]');
  const itemsNode = document.querySelector('[data-cart-items]');
  const footerNode = document.querySelector('[data-cart-footer]');
  const totalNode = document.querySelector('[data-cart-total]');
  const statusNode = document.querySelector('[data-cart-status]');
  const toast = document.querySelector('[data-cart-toast]');
  const addedNode = document.querySelector('[data-cart-added]');
  let toastTimer;
  let returnFocus;
  let lastAdded = '';
  let mutationQueue = Promise.resolve();
  let cartBusy = false;
  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[char]));
  const money = cents => new Intl.NumberFormat((window.SurfButta?.currency || 'AUD') === 'AUD' ? 'en-AU' : (document.documentElement.lang || 'en-AU'), {
    style: 'currency', currency: window.SurfButta?.currency || 'AUD'
  }).format((Number(cents) || 0) / 100);
  const announce = (message, isError = false) => {
    if (statusNode) {
      statusNode.textContent = message;
      statusNode.classList.toggle('is-error', isError);
    }
    if (!toast) return;
    toast.textContent = message;
    toast.classList.toggle('is-error', isError);
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.hidden = true; }, isError ? 8000 : 4000);
  };
  const enqueue = task => {
    const result = mutationQueue.then(task, task);
    mutationQueue = result.catch(() => {});
    return result;
  };
  const request = async (endpoint, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      let response;
      try {
        response = await fetch(`${root}${endpoint}`, {
          credentials: 'same-origin', ...options,
          headers: { Accept: 'application/json', ...options.headers }, signal: controller.signal
        });
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        // fetch only rejects when the request never reached the shop.
        const network = new Error(navigator.onLine === false
          ? 'You’re offline. Your selection is saved, so reconnect and try again.'
          : 'We couldn’t reach the shop. Your selection is saved, so please try again.');
        network.name = 'NetworkError';
        throw network;
      }
      let payload = null;
      try { payload = await response.json(); } catch (_) { payload = null; }
      if (!response.ok || !payload) {
        const error = new Error(payload?.description || payload?.message || 'Your bag couldn’t be updated. Please try again.');
        error.status = response.status;
        throw error;
      }
      return payload;
    } finally { clearTimeout(timeout); }
  };
  const itemMarkup = item => {
    const image = item.featured_image?.url || item.image;
    const title = escapeHtml(item.product_title);
    const key = escapeHtml(item.key);
    const url = escapeHtml(item.url);
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<span>${escapeHtml(item.variant_title)}</span>` : '';
    const sellingPlan = item.selling_plan_allocation?.selling_plan?.name;
    const subscription = sellingPlan ? `<span class="cart-selling-plan">${escapeHtml(sellingPlan)}</span>` : '';
    const properties = Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value).map(([key, value]) => `<span class="cart-line-property">${escapeHtml(key)}: ${escapeHtml(value)}</span>`).join('');
    return `<article class="cart-drawer-item${item.key === lastAdded ? ' is-added' : ''}" data-cart-line="${key}">
      <a class="cart-drawer-image" href="${url}" tabindex="-1" aria-hidden="true">
        ${image ? `<img src="${escapeHtml(image)}" width="120" height="120" alt="">` : ''}
      </a>
      <div class="cart-drawer-copy">
        <h3><a href="${url}">${title}</a></h3>${variant}${subscription}${properties}
        <strong>${money(item.final_line_price)}</strong>
        <div class="cart-line-actions">
          <div class="cart-quantity" role="group" aria-label="Quantity for ${title}">
            <button type="button" data-cart-change data-id="${key}" data-action="decrease" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity of ${title}">−</button>
            <span aria-live="polite">${item.quantity}</span>
            <button type="button" data-cart-change data-id="${key}" data-action="increase" data-quantity="${item.quantity + 1}" aria-label="Increase quantity of ${title}">+</button>
          </div>
          <button type="button" class="cart-remove" data-cart-change data-id="${key}" data-action="remove" data-quantity="0" aria-label="Remove ${title} from your bag">Remove</button>
        </div>
      </div>
    </article>`;
  };
  const renderCart = cart => {
    window.SurfButta.currency = cart.currency || window.SurfButta.currency;
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = cart.item_count; });
    document.querySelectorAll('[data-cart-open]').forEach(node => node.setAttribute('aria-label', `Open bag, ${cart.item_count} items`));
    if (!itemsNode || !footerNode) return;
    itemsNode.innerHTML = cart.items.length ? cart.items.map(itemMarkup).join('') : '<p class="cart-empty">Your bag is empty.</p>';
    footerNode.hidden = !cart.items.length;
    if (totalNode) totalNode.textContent = money(cart.total_price);
    if (addedNode) {
      const added = lastAdded && cart.items.find(item => item.key === lastAdded);
      addedNode.hidden = !added;
      if (added) {
        const variant = added.variant_title && added.variant_title !== 'Default Title' ? ` · ${added.variant_title}` : '';
        addedNode.querySelector('[data-cart-added-text]').textContent = `${added.product_title}${variant}`;
      }
    }
    document.dispatchEvent(new CustomEvent('sb:cart', { detail: cart }));
  };
  const refreshCart = async () => {
    const cart = await request('cart.js');
    renderCart(cart);
    return cart;
  };
  const openCart = async trigger => {
    if (!drawer || typeof drawer.showModal !== 'function') {
      window.location.assign(`${root}cart`);
      return;
    }
    if (!drawer.open) {
      returnFocus = trigger || document.activeElement;
      drawer.showModal();
    }
    document.body.classList.add('cart-open');
    if (statusNode) { statusNode.textContent = 'Loading your bag…'; statusNode.classList.remove('is-error'); }
    try {
      await mutationQueue;
      await refreshCart();
      if (statusNode) statusNode.textContent = '';
    } catch (_) {
      announce('Your bag couldn’t refresh. Open “View full bag” to check your order.', true);
    }
  };
  const closeCart = () => { if (drawer?.open) drawer.close(); };
  const setCartBusy = busy => {
    cartBusy = busy;
    drawer?.setAttribute('aria-busy', String(busy));
    drawer?.querySelectorAll('[data-cart-change], [name="checkout"]').forEach(node => { node.disabled = busy; });
  };
  document.addEventListener('click', async event => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openCart(opener);
      return;
    }
    if (event.target.closest('[data-cart-close]')) { closeCart(); return; }
    const control = event.target.closest('[data-cart-change]');
    if (!control || cartBusy) return;
    const id = control.dataset.id;
    const action = control.dataset.action;
    setCartBusy(true);
    try {
      const cart = await enqueue(() => request('cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity: Number(control.dataset.quantity) })
      }));
      renderCart(cart);
      const nextFocus = [...drawer.querySelectorAll('[data-cart-change]')].find(node => node.dataset.id === id && node.dataset.action === action);
      setCartBusy(false);
      (nextFocus || drawer.querySelector('[data-cart-close]'))?.focus();
      announce(action === 'remove' ? 'Removed from your bag.' : 'Bag updated.');
    } catch (error) {
      announce(error.name === 'AbortError' ? 'The update timed out. View your full bag before retrying.' : error.message, true);
      // Show the bag as the shop has it, so a failed change never leaves a wrong quantity on screen.
      refreshCart().catch(() => {});
    } finally { setCartBusy(false); }
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('.js-product-form');
    if (!form) return;
    event.preventDefault();
    if (form.dataset.submitting === 'true' || !form.reportValidity()) return;
    const button = form.querySelector('[type="submit"]');
    if (!button || button.disabled) return;
    const formData = new FormData(form);
    const originalText = button.innerHTML;
    const formError = form.querySelector('[data-form-error]');
    if (formError) { formError.hidden = true; formError.textContent = ''; }
    form.dataset.submitting = 'true';
    button.disabled = true;
    button.textContent = 'Adding…';
    const variantSelect = form.querySelector('.variant-select');
    if (variantSelect) variantSelect.disabled = true;
    try {
      const item = await enqueue(() => request('cart/add.js', { method: 'POST', body: formData }));
      lastAdded = item.key || '';
      // Refresh failures must not be reported as failed adds: that invites duplicate orders.
      await openCart(button);
      if (!statusNode?.textContent) announce(`${item.product_title || 'Item'} added to your bag.`);
    } catch (error) {
      const message = error.name === 'AbortError' ? 'This is taking too long. Check your bag before trying again, so nothing is added twice.' : error.message;
      announce(message, true);
      if (formError) { formError.textContent = message; formError.hidden = false; }
    } finally {
      form.dataset.submitting = 'false';
      button.innerHTML = originalText;
      button.disabled = false;
      if (variantSelect) variantSelect.disabled = false;
    }
  });
  if (drawer) {
    drawer.addEventListener('click', event => {
      if (event.target !== drawer) return;
      const bounds = drawer.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeCart();
    });
    drawer.addEventListener('close', () => {
      lastAdded = '';
      if (addedNode) addedNode.hidden = true;
      document.body.classList.remove('cart-open');
      if (returnFocus?.isConnected && !returnFocus.disabled) returnFocus.focus();
    });
  }
  // Checkout hand-off: one tap, a clear busy state, and a working button again
  // if the shopper comes back with the browser's back button.
  document.addEventListener('submit', event => {
    const checkout = event.submitter?.name === 'checkout' ? event.submitter : null;
    if (!checkout) return;
    if (checkout.dataset.busy === 'true') { event.preventDefault(); return; }
    checkout.dataset.busy = 'true';
    checkout.dataset.label = checkout.innerHTML;
    checkout.setAttribute('aria-busy', 'true');
    checkout.textContent = 'Opening secure checkout…';
    // Submitting the form must still send name="checkout", so the button stays enabled.
    setTimeout(() => {
      if (document.visibilityState === 'visible' && checkout.dataset.busy === 'true') resetCheckout();
    }, 12000);
  });
  const resetCheckout = () => document.querySelectorAll('[name="checkout"][data-busy="true"]').forEach(node => {
    node.dataset.busy = 'false';
    node.removeAttribute('aria-busy');
    if (node.dataset.label) node.innerHTML = node.dataset.label;
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    resetCheckout();
    refreshCart().catch(() => {});
  });
  // Theme editor: keep the bag count right after a section reloads.
  document.addEventListener('shopify:section:load', () => { refreshCart().catch(() => {}); });
  document.querySelectorAll('[data-bundle-picker]').forEach(picker => {
    const form = picker.closest('form');
    const variants = form.querySelector('.variant-select');
    if (!variants) return;
    const fields = [...picker.querySelectorAll('[data-bundle-colour]')];
    const colours = ['Red', 'Yellow', 'Green', 'Blue', 'Pink', 'Orange', 'Purple'];
    const selected = variants.selectedOptions[0].dataset.title.split(' + ');
    fields.forEach((field, i) => { field.value = selected[i] || colours[i]; });
    const art = form.closest('.product-detail-hero')?.querySelector('[data-bundle-art]');
    const updatePhotos = () => {
      if (!art) return;
      const photos = [...art.querySelectorAll('img')].slice(Number(art.dataset.fixedBars));
      fields.forEach((field, i) => {
        const selectedOption = field.selectedOptions[0];
        if (!photos[i] || !selectedOption.dataset.photo) return;
        photos[i].removeAttribute('srcset');
        photos[i].src = selectedOption.dataset.photo;
        photos[i].alt = selectedOption.value;
      });
    };
    const update = () => {
      updatePhotos();
      const selection = fields.map(field => field.value).sort((a, b) => colours.indexOf(a) - colours.indexOf(b)).join(' + ');
      const option = [...variants.options].find(option => option.dataset.title === selection);
      if (!option) return;
      variants.value = option.value;
      variants.dispatchEvent(new Event('change', { bubbles: true }));
      picker.querySelector('[data-bundle-status]').textContent = selection;
    };
    fields.forEach(field => field.addEventListener('change', update));
    variants.hidden = true;
    const label = form.querySelector(`label[for="${variants.id}"]`);
    if (label) label.hidden = true;
    picker.hidden = false;
    updatePhotos();
    picker.querySelector('[data-bundle-status]').textContent = selected.join(' + ');
  });
  document.addEventListener('change', event => {
    const select = event.target.closest('.variant-select');
    if (!select) return;
    const option = [...select.options].find(candidate => candidate.value === select.value);
    if (!option) return;
    const form = select.closest('form');
    const price = form.querySelector('[data-product-price]');
    if (price) price.textContent = money(option.dataset.price).replace(/\.00$/, '');
    const button = form.querySelector('[name="add"]');
    button.disabled = option.dataset.available !== 'true';
    const label = button.querySelector('[data-add-label]');
    if (label) label.textContent = button.disabled ? 'Sold out' : 'Add to bag';
    else button.innerHTML = button.disabled ? 'Sold out' : 'Add to bag <span aria-hidden="true">+</span>';
    form.dataset.available = String(!button.disabled);
    const image = form.closest('.product-detail-hero')?.querySelector('.product-variant-image');
    if (image && option.dataset.imageSrc) {
      image.srcset = option.dataset.imageSrcset;
      image.src = option.dataset.imageSrc;
      image.alt = option.dataset.imageAlt || '';
      image.width = Number(option.dataset.imageWidth);
      image.height = Number(option.dataset.imageHeight);
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
    document.dispatchEvent(new CustomEvent('sb:variant', { detail: {
      form, id: option.value, title: option.dataset.title, price: Number(option.dataset.price), available: !button.disabled
    } }));
  });
})();